<template>
  <div class="serviceUrl">
    <el-row>
      <el-col :span="5">
        <span class="label">
          JAVA ARCHIVE URL
        </span>
      </el-col>
      <el-col :span="18">
        <el-input
          :disabled="sameAsMainDomain"
          v-model="serviceUrl"
          placeholder="Please provide the Web service URL for the uploaded file if it is different from the main domain."
        ></el-input>
        <div class="switchWrapper">
          <span>Same as TARGET SERVICE URL</span>
          <el-switch v-model="sameAsMainDomain">
          </el-switch>
        </div>
        <el-tag class="infoTag">
          <div>
            The <span class="strong">JAVA ARCHIVE URL</span> corresponds to the uploaded archive file. Since it may occasionally differ from the <span class="strong">TARGET SERVICE URL "{{taskData.sandboxHomePageUrl}}"</span>, please provide the URL if it is from a different domain. If it matches the <span class="strong">TARGET SERVICE URL</span>, click the toggle switch to turn it <span class="strong">BLACK</span> and then click 'NEXT' to skip this step.
          </div>
        </el-tag>
        <div class="infoWrapper">
          <div class="infoContent">
            <div
              class="progressWrapper"
              v-if="status === 'connecting'"
            >
              <progress-bar :done="status !== 'connecting'" />
              <p class="textCenter">Connecting to {{serviceUrl}}</p>
            </div>
            <div
              class="successWrapper"
              v-if="status === 'success'"
            >
              <div class="successContent">
                <i class="el-icon-success successIcon"></i>
                <span>Connection to the SERVICE URL has succeeded!</span>
                <el-button
                  style="margin-left: 20px"
                  type="success"
                  size="small"
                  @click="retry"
                >RETRY</el-button>
              </div>
              <div class="imageWrapper">
                <el-card style="width: 800px;height: 300px;">
                  <el-image
                    :src="src"
                    :preview-src-list="[src]"
                  />
                </el-card>
              </div>
            </div>
            <div
              class="errorWrapper"
              v-if="status === 'error'"
            >
              <div class="errorContent">
                <p> <i class="el-icon-error errorIcon"></i> Unable to connect to the target server. Invoking network diagnostics.</p>
                <div class="diagList">
                  <div
                    v-for="stage in stageList"
                    :key="stage.stageName"
                  >
                    <i :class="{
                        stageIcon: true,
                        ['el-icon-success']: stage.status !== 'FAIL',
                        successIcon: stage.status === 'PASS',
                         ['el-icon-error']: stage.status === 'FAIL',
                          errorIcon:  stage.status === 'FAIL'}">
                    </i>
                    <span>
                      {{stage.resultInfo ?? stage.description }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="['success', 'error'].includes(status)">
              <div :class="['errorTip', {success: status === 'success'}]">
                If the target service has firewall protection enabled, please kindly append our IP address to the whitelist and allow all inbound
                and outbound network traffic for it.
              </div>
              <div class="ipWrapper">
                <span class="label ipLabel">Our source IP
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :visible-arrow="false"
                    placement="top"
                    popper-class="targetService-popper"
                  >
                    <div slot="content">
                      ZAST.AI creates proof-of-concept code for each vulnerability to verify exploit-ability. If your target service is protected by a
                      firewall, the IP address’s appearance on the firewall whitelist, or other equally effective methods, provides access to the
                      target
                      service to allow proper security analysis. Since certain vulnerabilities do not respond directly, please ensure to enable your
                      server to access the external IPs (outbound) so that the confirmation of the exploitability could be successfully communicated
                      back to us for full report assembly purpose.
                    </div>
                    <i class="el-icon-question questionIcon"></i>
                  </el-tooltip>
                </span>
                <clip-text class="ipContent">
                  {{this.sourceIp.ipv4}}
                </clip-text>
                <clip-text>
                  {{this.sourceIp.ipv6}}
                </clip-text>
              </div>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import ClipText from '@/components/clip-text.vue';
import ProgressBar from '@/components/progress-bar.vue';
import { createNetworkDiagnosis, getNetworkDiagnosis, getWebsiteScreenshot } from '@/api/base';

export default {
  name: 'ServiceUrl',
  inject: ['taskData', 'sourceIp'],
  components: {
    ClipText,
    ProgressBar,
  },
  data() {
    return {
      serviceUrl: this.taskData.sandboxHomePageUrl,
      sameAsMainDomain: true,
      stageList: [
        {
          stageName: 'DNS',
          description: 'DNS resolution',
          status: 'NOT_STARTED',
        },
        {
          stageName: 'PORT',
          description: 'port state check',
          status: 'NOT_STARTED',
        },
        {
          stageName: 'HTTP&HTTPS',
          description: 'http & https server check',
          status: 'NOT_STARTED',
        },
      ],
      status: '',
      src: '',
    };
  },
  watch: {
    sameAsMainDomain(val) {
      if (val) {
        this.serviceUrl = this.taskData.sandboxHomePageUrl;
      }

      this.status = '';
    },
  },
  methods: {
    retry() {
      this.status = 'connecting';

      this.validate().then(this.getWebsiteScreenshot).catch(this.createNetworkDiagnosis);
    },
    validate() {
      return new Promise((resolve, reject) => {
        this.$refs.form.validate((valid) => {
          if (!valid) {
            return reject();
          }

          return resolve();
        });
      });
    },
    getWebsiteScreenshot() {
      return getWebsiteScreenshot({ sandboxUrl: this.serviceUrl }).then((resp) => {
        const blob = new Blob([resp], { type: 'image/png' });
        this.src = URL.createObjectURL(blob);
        this.status = 'success';
      });
    },
    createNetworkDiagnosis() {
      this.status = 'error';
      return createNetworkDiagnosis({ testUrl: this.serviceUrl }).then(({ id }) => this.getNetworkDiagnosis(id));
    },
    getNetworkDiagnosis(id, params = {}) {
      return getNetworkDiagnosis(id, params).then((response) => {
        const { stages, status } = response;

        this.stageList = stages;

        if (status === 'FINISHED') {
          return;
        }

        return this.getNetworkDiagnosis(id);
      });
    },
    next() {
      if (this.status === 'connecting') {
        return;
      }

      if (this.status === 'success') {
        return { stage: 'ServiceUrl', data: { serviceUrl: this.serviceUrl } };
      }

      if (this.sameAsMainDomain || this.taskData.sandboxHomePageUrl === this.serviceUrl) {
        return { stage: 'ServiceUrl', data: { sameAsMainDomain: true } };
      }

      this.status = 'connecting';

      this.getWebsiteScreenshot().catch(this.createNetworkDiagnosis);

      return;
    },
  },
};
</script>

<style lang="scss" scoped>
.infoTag {
  margin-top: 16px;
  height: auto;
  overflow: hidden;
  white-space: normal;
  font-size: 14px;
  padding: 8px 16px;

  span {
    font-weight: bold;
  }
}

.label {
  font-weight: bold;
  line-height: 40px;
}

.switchWrapper {
  display: flex;
  align-items: center;
  margin-top: 20px;

  span {
    margin-right: 10px;
  }
}

.serviceUrl {
  .row {
    line-height: 40px;
  }

  .rowCenter {
    display: flex;
    align-items: center;
  }

  .label {
    font-weight: bold;
  }

  .placeholder {
    display: block;
    height: 20px;
  }

  .info {
    color: #909399;
  }

  .progress {
    margin-top: 20px;
    width: 80%;
    margin: 0 auto;
  }

  .infoWrapper {
    margin-top: 40px;
  }

  .textCenter {
    text-align: center;
  }

  .errorContent {
    width: fit-content;
    margin: 0 auto;
  }

  .stageIcon {
    font-size: 20px;
    color: #909399;
  }

  .errorIcon {
    color: #f56c6c;
    font-size: 20px;
  }

  .successIcon {
    color: #67c23a;
    font-size: 20px;
  }

  .diagList {
    line-height: 2em;
  }

  .errorTip {
    color: #ff001f;
    margin-top: 20px;

    &.success {
      color: #909399;
    }
  }

  .ipWrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ipLabel {
    margin-right: 20px;
  }

  .ipContent {
    margin-right: 40px;
  }

  .successContent {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .el-icon-success {
      margin-right: 40px;
    }
  }

  .ipText {
    margin-right: 20px;
    font-weight: bold;
  }

  .questionIcon {
    display: inline-block;
  }

  .imageWrapper {
    display: flex;
    justify-content: center;
  }
}
</style>